import { useCallback, useEffect, useRef, useState } from 'react';

import { Notification } from 'stores/notifications/notifications';

import { buildStackGeometry, StackPlacement } from './helpers';

export type NotificationLayout = StackPlacement & {
  notification: Notification;
  leaving: boolean;
};

type UseNotificationStackArgs = {
  notifications: Array<Notification>;
  expanded: boolean;
  /** Ids that are animating out. They keep their slot but stop pushing the others around. */
  dismissing: Set<string>;
};

export const useNotificationStack = ({ notifications, expanded, dismissing }: UseNotificationStackArgs) => {
  const [heights, setHeights] = useState<Record<string, number>>({});

  const observerRef = useRef<ResizeObserver | null>(null);
  const elementIds = useRef(new WeakMap<Element, string>());
  const refCallbacks = useRef(new Map<string, (element: HTMLElement | null) => void>());
  const lastOffsets = useRef<Record<string, number>>({});

  const getObserver = useCallback(() => {
    if (!observerRef.current) {
      observerRef.current = new ResizeObserver((entries) => {
        setHeights((previous) => {
          const next = { ...previous };
          let changed = false;

          for (const entry of entries) {
            const id = elementIds.current.get(entry.target);
            const height = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.target.clientHeight);

            if (id && height && next[id] !== height) {
              next[id] = height;
              changed = true;
            }
          }

          return changed ? next : previous;
        });
      });
    }

    return observerRef.current;
  }, []);

  // Ref callbacks are memoised per id — a fresh function on every render would make React detach and
  // reattach the element, churning the observer on every keystroke of state.
  const registerCard = useCallback(
    (id: string) => {
      const existing = refCallbacks.current.get(id);

      if (existing) {
        return existing;
      }

      const callback = (element: HTMLElement | null) => {
        if (!element) {
          return;
        }

        elementIds.current.set(element, id);
        getObserver().observe(element);

        // Seed synchronously so the first paint is not at height zero. Ref callbacks run during
        // commit, so this re-render lands before the browser paints.
        const height = element.clientHeight;

        if (height) {
          setHeights((previous) => (previous[id] === height ? previous : { ...previous, [id]: height }));
        }
      };

      refCallbacks.current.set(id, callback);

      return callback;
    },
    [getObserver],
  );

  useEffect(() => {
    const observer = observerRef.current;

    return () => observer?.disconnect();
  }, []);

  // Drop bookkeeping for notifications that are fully gone.
  useEffect(() => {
    const liveIds = new Set(notifications.map((notification) => notification.id));

    refCallbacks.current.forEach((_, id) => {
      if (!liveIds.has(id)) {
        refCallbacks.current.delete(id);
        delete lastOffsets.current[id];
      }
    });

    setHeights((previous) => {
      const staleIds = Object.keys(previous).filter((id) => !liveIds.has(id));

      if (staleIds.length === 0) {
        return previous;
      }

      const next = { ...previous };
      staleIds.forEach((id) => delete next[id]);

      return next;
    });
  }, [notifications]);

  const settled = notifications.filter((notification) => !dismissing.has(notification.id));
  const newestFirst = settled.slice().reverse();
  const { placements, canvasHeight } = buildStackGeometry(
    newestFirst.map((notification) => ({ id: notification.id, height: heights[notification.id] ?? 0 })),
    expanded,
  );

  const placementById = new Map(placements.map((placement) => [placement.id, placement]));

  useEffect(() => {
    placements.forEach((placement) => {
      lastOffsets.current[placement.id] = placement.offsetY;
    });
  });

  // Rendered in store order, never re-sorted: React would otherwise move the DOM nodes around, which
  // restarts CSS animations and kills transitions mid-flight.
  const layouts: Array<NotificationLayout> = notifications.map((notification) => {
    const placement = placementById.get(notification.id);

    if (placement) {
      return { ...placement, notification, leaving: false };
    }

    return {
      id: notification.id,
      notification,
      leaving: true,
      offsetY: lastOffsets.current[notification.id] ?? 0,
      scaleX: 0.96,
      height: heights[notification.id] ?? 0,
      opacity: 0,
      zIndex: 0,
      interactive: false,
    };
  });

  return { layouts, canvasHeight, registerCard };
};
